import { createClient } from './supabase/client'

/**
 * Authenticated fetch wrapper for API calls.
 * Retries once on missing token (hard-refresh race) and on 401 (stale token).
 * Skips 401 retry for non-replayable bodies (FormData, streams).
 * Redirects to billing on subscription-402 (P0-4 Phase 2, 2026-06-11).
 */

// P0-4 Phase 2 (11.06.2026): API blockt expired/suspended Abos global mit 402
// + redirect-Hint (lib/require-active-subscription.ts). Ohne zentrales Handling
// blieben Nutzer auf still fehlschlagenden Fetches sitzen statt auf der
// Billing-Seite zu landen. Throttle gegen Redirect-Sturm: viele parallele
// Fetches einer Seite kriegen alle 402 — nur der erste navigiert.
const SUBSCRIPTION_402_ERRORS = new Set([
  'subscription_required',
  'subscription_expired',
  'subscription_suspended',
])
let lastSubscriptionRedirectAt = 0

// TOTP-Gate 29.07.2026: seit fc2993f4 antworten die geschuetzten Routen mit
// 403 + code:'mfa-required', wenn ein Token nur aal1 traegt, der Nutzer aber
// einen verifizierten TOTP-Faktor hat (lib/check-permission.ts requireUser).
// Vor diesem Handler kannte NUR der Login-Pfad diese Antwort — jede andere
// Seite deutete das 403 als Berechtigungsfehler. Gemessener Vorfall:
// routes/_dashboard/sms.tsx:394 las jedes 403 als "du bist kein Super-Admin"
// und blendete den Admin-Bereich aus; loadLogs prueft den Status gar nicht und
// zeigte still eine leere Liste. Beides falsche Diagnosen — es fehlt der zweite
// Faktor, nicht die Berechtigung.
//
// Gleiche Bauart wie handleSubscription402 darunter: clone(), Code lesen,
// einmal navigieren, Throttle gegen den Redirect-Sturm paralleler Fetches.
// Ziel ist /login: der Recovery-Waechter dort (routes/_auth/login.tsx) faellt
// bei nicht ausstellbarem Cookie auf das Anmeldeformular zurueck, und der
// normale Anmeldeweg fuehrt ueber mfaType='totp' in den TOTP-Schritt. Kein
// Schleifenrisiko: /login selbst ruft keine gate-geschuetzte Route.
let lastMfaRedirectAt = 0

export async function handleMfaRequired403(res: Response): Promise<void> {
  if (res.status !== 403 || typeof window === 'undefined') return
  const now = Date.now()
  if (now - lastMfaRedirectAt < 5000) return
  // clone(): Body für den eigentlichen Caller unangetastet lassen.
  const body = (await res
    .clone()
    .json()
    .catch(() => null)) as { code?: string } | null
  if (body?.code !== 'mfa-required') return
  // Schon auf dem Anmeldeweg → nicht erneut navigieren.
  if (window.location.pathname.startsWith('/login')) return
  lastMfaRedirectAt = now
  window.location.assign('/login')
}

async function handleSubscription402(res: Response): Promise<void> {
  if (res.status !== 402 || typeof window === 'undefined') return
  const now = Date.now()
  if (now - lastSubscriptionRedirectAt < 5000) return
  // clone(): Body für den eigentlichen Caller unangetastet lassen.
  const body = (await res
    .clone()
    .json()
    .catch(() => null)) as { code?: string; error?: string; redirect?: string } | null
  // Welle 2.3 Fehlervertrag (27.07.2026): `code` ist das kanonische Feld,
  // `error` bleibt Fallback für gecachte/alte API-Antworten während des
  // Rollouts (SPA + API deployen nicht atomar).
  if (!body || !SUBSCRIPTION_402_ERRORS.has(body.code ?? body.error ?? '')) return
  const target = body.redirect || '/billing'
  // Bereits auf einer Billing-Route → kein Redirect (Seite selbst nutzt
  // nur whitelisted /api/billing-Calls, aber Widgets können 402 ziehen).
  if (window.location.pathname.startsWith('/billing')) return
  lastSubscriptionRedirectAt = now
  window.location.assign(target)
}

export async function apiFetch(url: string, opts: RequestInit = {}): Promise<Response> {
  const supabase = createClient()

  let { data } = await supabase.auth.getSession()
  let token = data?.session?.access_token

  // On hard refresh, getSession() may return null. Try refreshSession which also handles expired tokens.
  if (!token) {
    const { data: refreshed } = await supabase.auth.refreshSession()
    token = refreshed?.session?.access_token
  }

  const headers = new Headers(opts.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  // Welle 2.3 Schritt 2 (27.07.2026): honos json-Validator verwirft Bodys
  // ohne application/json Content-Type still als {} (gemessen, s.
  // error-contract.test.ts). Browser-fetch defaultet String-Bodys auf
  // text/plain — daher hier zentral JSON defaulten statt an ~100
  // Callsites einzeln. Greift nur wenn der Caller nichts gesetzt hat;
  // FormData/Blob/URLSearchParams setzen ihren Content-Type selbst.
  if (typeof opts.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(url, { ...opts, headers })

  await handleSubscription402(res)
  await handleMfaRequired403(res)

  // 401 retry — only for replayable bodies (not FormData/Blob/streams)
  if (res.status === 401 && token) {
    const bodyIsReplayable =
      opts.body == null || typeof opts.body === 'string' || opts.body instanceof URLSearchParams

    if (bodyIsReplayable) {
      const { data: refreshed } = await supabase.auth.refreshSession()
      const freshToken = refreshed?.session?.access_token
      if (freshToken && freshToken !== token) {
        headers.set('Authorization', `Bearer ${freshToken}`)
        return fetch(url, { ...opts, headers })
      }
    }
  }

  return res
}
